import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { clsx } from 'clsx';
import {
  MONTHS_PT,
  WEEKDAYS_PT,
  isoToBR,
  brToISO,
  applyMask,
  parseISO,
  isoForDay,
  buildMonthGrid,
  isDayDisabled,
} from './dateInputUtils';

/**
 * DateInputBR
 *
 * Substitui o `<input type="date">` nativo (que respeita o locale do navegador
 * e frequentemente exibe MM/DD/AAAA e o calendário em inglês) por um campo que
 * sempre apresenta a data no formato brasileiro DD/MM/AAAA e abre um seletor de
 * calendário próprio, totalmente em português do Brasil (nomes de meses e dias
 * da semana em pt-BR), independente do idioma do navegador.
 *
 * - `value` continua sendo `YYYY-MM-DD` (ou string vazia).
 * - `onChange` recebe a nova string ISO (ou '' quando limpa).
 */
export interface DateInputBRProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: string;
  onChange: (isoValue: string) => void;
  /** Não exibe o botão do calendário (apenas a máscara DD/MM/AAAA). */
  hideCalendarButton?: boolean;
}

export const DateInputBR = forwardRef<HTMLInputElement, DateInputBRProps>(function DateInputBR(
  {
    value,
    onChange,
    className,
    placeholder,
    disabled,
    hideCalendarButton,
    min,
    max,
    ...rest
  },
  ref
) {
  const [display, setDisplay] = useState<string>(() => isoToBR(value));
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Mês/ano atualmente exibidos no calendário.
  const today = new Date();
  const [viewYear, setViewYear] = useState<number>(today.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(today.getMonth());

  useEffect(() => {
    const currentISO = brToISO(display);
    if (currentISO !== value) {
      setDisplay(isoToBR(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Fecha o popup ao clicar fora ou pressionar Esc.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyMask(e.target.value);
    setDisplay(masked);
    if (masked === '') {
      onChange('');
      return;
    }
    const iso = brToISO(masked);
    if (iso) onChange(iso);
  };

  const handleBlur = () => {
    if (display === '') {
      if (value) onChange('');
      return;
    }
    const iso = brToISO(display);
    if (!iso) {
      // Entrada incompleta/ inválida — restaura último valor consistente.
      setDisplay(isoToBR(value));
    }
  };

  const toggleCalendar = () => {
    if (disabled) return;
    if (!open) {
      // Posiciona o calendário no mês/ano do valor atual (ou hoje).
      const parsed = parseISO(value);
      if (parsed) {
        setViewYear(parsed.year);
        setViewMonth(parsed.month);
      } else {
        const now = new Date();
        setViewYear(now.getFullYear());
        setViewMonth(now.getMonth());
      }
    }
    setOpen((o) => !o);
  };

  const goPrevMonth = () => {
    setViewMonth((m) => {
      if (m === 0) { setViewYear((y) => y - 1); return 11; }
      return m - 1;
    });
  };
  const goNextMonth = () => {
    setViewMonth((m) => {
      if (m === 11) { setViewYear((y) => y + 1); return 0; }
      return m + 1;
    });
  };

  const selectDay = (day: number) => {
    const iso = isoForDay(viewYear, viewMonth, day);
    if (isDayDisabled(iso, min as string | undefined, max as string | undefined)) return;
    setDisplay(isoToBR(iso));
    onChange(iso);
    setOpen(false);
  };

  // Monta a grade do mês (com células vazias no início).
  const cells = buildMonthGrid(viewYear, viewMonth);

  const selected = parseISO(value);

  return (
    <div
      ref={containerRef}
      className={clsx('relative inline-block', className?.includes('w-full') ? 'w-full' : '')}
    >
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder ?? 'dd/mm/aaaa'}
        value={display}
        onChange={handleChange}
        onBlur={handleBlur}
        maxLength={10}
        disabled={disabled}
        className={clsx(className, !hideCalendarButton && 'pr-8')}
        {...rest}
      />
      {!hideCalendarButton && (
        <button
          type="button"
          tabIndex={-1}
          onClick={toggleCalendar}
          disabled={disabled}
          aria-label="Abrir calendário"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:opacity-40"
        >
          <Calendar size={14} />
        </button>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-64 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
          {/* Cabeçalho: mês/ano + navegação */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={goPrevMonth}
              aria-label="Mês anterior"
              className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-slate-700">
              {MONTHS_PT[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={goNextMonth}
              aria-label="Próximo mês"
              className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Dias da semana */}
          <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[11px] font-medium text-slate-400">
            {WEEKDAYS_PT.map((wd) => (
              <div key={wd} className="py-1">{wd}</div>
            ))}
          </div>

          {/* Grade de dias */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((day, idx) => {
              if (day === null) return <div key={`e-${idx}`} />;
              const iso = isoForDay(viewYear, viewMonth, day);
              const isSelected = !!selected && selected.year === viewYear && selected.month === viewMonth && selected.day === day;
              const isToday = today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;
              const dayDisabled = isDayDisabled(iso, min as string | undefined, max as string | undefined);
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={dayDisabled}
                  onClick={() => selectDay(day)}
                  className={clsx(
                    'h-8 w-8 rounded text-sm transition-colors',
                    isSelected
                      ? 'bg-emerald-600 text-white font-semibold'
                      : isToday
                      ? 'border border-emerald-400 text-emerald-700 hover:bg-emerald-50'
                      : 'text-slate-700 hover:bg-slate-100',
                    dayDisabled && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                  )}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

export default DateInputBR;
